import React, { FC, useMemo, useEffect } from "react";

interface UserAvatarProps {
  name: string;
  size?: number;
  className?: string;
}

// Load Google Font for avatar
const loadFont = () => {
  const fontId = "avatar-google-font";
  if (!document.getElementById(fontId)) {
    const link = document.createElement("link");
    link.id = fontId;
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Outfit:wght@600;700&display=swap";
    document.head.appendChild(link);
  }
};

// Generate consistent values based on the name
const getAvatarStyle = (name: string) => {
  const gradients: [string, string][] = [
    ["#667eea", "#764ba2"], // Purple
    ["#f093fb", "#f5576c"], // Pink
    ["#4facfe", "#00f2fe"], // Blue
    ["#43e97b", "#38f9d7"], // Green
    ["#fa709a", "#fee140"], // Orange-Pink
    ["#ff6b6b", "#feca57"], // Coral-Yellow
    ["#a55eea", "#8854d0"], // Violet
    ["#26de81", "#20bf6b"], // Emerald
    ["#fd9644", "#fc5c65"], // Sunset
    ["#45aaf2", "#4b7bec"], // Ocean
    ["#eb3b5a", "#fc5c65"], // Ruby
    ["#20bf6b", "#0fb9b1"], // Teal
  ];

  // Create a hash from the name
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  const colorIndex = Math.abs(hash) % gradients.length;
  const patternIndex = Math.abs(hash >> 4) % 6; // 6 different patterns

  return {
    colors: gradients[colorIndex],
    pattern: patternIndex,
    // Generate pseudo-random positions for decorative elements
    seed1: Math.abs((hash >> 2) % 30) + 10,
    seed2: Math.abs((hash >> 3) % 30) + 60,
    seed3: Math.abs((hash >> 5) % 20) + 5,
  };
};

const UserAvatar: FC<UserAvatarProps> = ({ name, size = 40, className = "" }) => {
  useEffect(() => {
    loadFont();
  }, []);

  const initial = useMemo(() => {
    if (!name || name.trim() === "") return "?";
    return name.trim().charAt(0).toUpperCase();
  }, [name]);

  const style = useMemo(() => getAvatarStyle(name || ""), [name]);
  const [startColor, endColor] = style.colors;

  // Create unique IDs to avoid conflicts when multiple avatars are rendered
  const uid = useMemo(() => Math.random().toString(36).substr(2, 9), []);
  const gradientId = `avatar-grad-${uid}`;
  const patternId = `avatar-pattern-${uid}`;

  const renderPattern = () => {
    const { pattern, seed1, seed2, seed3 } = style;

    switch (pattern) {
      case 0: // Floating circles
        return (
          <g opacity="0.3">
            <circle cx={seed1} cy={seed1} r="25" fill="white" />
            <circle cx={seed2} cy={seed3} r="18" fill="white" />
            <circle cx={seed2 + 20} cy={seed2 + 10} r="30" fill="white" />
            <circle cx={seed3} cy={seed2} r="12" fill="white" />
          </g>
        );
      case 1: // Diagonal stripes
        return (
          <g opacity="0.15">
            <defs>
              <pattern id={patternId} patternUnits="userSpaceOnUse" width="20" height="20" patternTransform="rotate(45)">
                <rect width="10" height="20" fill="white" />
              </pattern>
            </defs>
            <rect width="100" height="100" fill={`url(#${patternId})`} />
          </g>
        );
      case 2: // Geometric triangles
        return (
          <g opacity="0.25">
            <polygon points={`0,100 ${seed1 + 30},100 0,${seed2}`} fill="white" />
            <polygon points={`100,0 100,${seed1 + 20} ${seed2},0`} fill="white" />
            <polygon points={`${seed2 - 10},100 100,100 100,${seed2 + 10}`} fill="white" />
          </g>
        );
      case 3: // Concentric arcs
        return (
          <g opacity="0.2">
            <circle cx="0" cy="100" r="60" fill="none" stroke="white" strokeWidth="15" />
            <circle cx="100" cy="0" r="45" fill="none" stroke="white" strokeWidth="10" />
            <circle cx="100" cy="100" r="35" fill="none" stroke="white" strokeWidth="8" />
          </g>
        );
      case 4: // Dots grid
        return (
          <g opacity="0.2">
            {[15, 35, 55, 75].map((x) =>
              [15, 35, 55, 75].map((y) => (
                <circle key={`${x}-${y}`} cx={x} cy={y} r="4" fill="white" />
              ))
            )}
            <circle cx={seed1} cy={seed2} r="20" fill="white" opacity="0.5" />
          </g>
        );
      case 5: // Wave pattern
        return (
          <g opacity="0.2">
            <path
              d="M-10,50 Q25,20 50,50 T110,50"
              fill="none"
              stroke="white"
              strokeWidth="20"
            />
            <path
              d="M-10,80 Q25,50 50,80 T110,80"
              fill="none"
              stroke="white"
              strokeWidth="15"
            />
            <circle cx={seed2} cy={seed3} r="15" fill="white" />
          </g>
        );
      default:
        return null;
    }
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      className={className}
      style={{ borderRadius: "8px", overflow: "hidden" }}
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={startColor} />
          <stop offset="100%" stopColor={endColor} />
        </linearGradient>
        <clipPath id={`clip-${uid}`}>
          <rect width="100" height="100" rx="8" ry="8" />
        </clipPath>
      </defs>

      <g clipPath={`url(#clip-${uid})`}>
        {/* Background gradient */}
        <rect width="100" height="100" fill={`url(#${gradientId})`} />

        {/* Decorative pattern */}
        {renderPattern()}

        {/* Subtle inner shadow/glow */}
        <rect
          width="100"
          height="100"
          fill="none"
          stroke="rgba(255,255,255,0.3)"
          strokeWidth="2"
          rx="8"
          ry="8"
        />
      </g>

      {/* Initial letter with shadow */}
      <text
        x="50"
        y="54"
        dominantBaseline="central"
        textAnchor="middle"
        fill="rgba(0,0,0,0.25)"
        fontSize="50"
        fontWeight="600"
        fontFamily="'Outfit', -apple-system, BlinkMacSystemFont, sans-serif"
        letterSpacing="-1"
      >
        {initial}
      </text>
      <text
        x="50"
        y="52"
        dominantBaseline="central"
        textAnchor="middle"
        fill="white"
        fontSize="50"
        fontWeight="600"
        fontFamily="'Outfit', -apple-system, BlinkMacSystemFont, sans-serif"
        letterSpacing="-1"
      >
        {initial}
      </text>
    </svg>
  );
};

export { UserAvatar };
